import cn from 'classnames';
import { Formik } from 'formik';
import * as yup from 'yup';

import { Button } from '@/components/button';
import Icon from '@/components/icon/Icon';
import { Dropdown, Modal } from 'react-bootstrap';
import { useState } from 'react';
import { Control } from '@/components/form';
import {
  LarkiErrorResponse,
  useGenerate2dPlanOnMapMutation,
  useGet2dPlansOnMapQuery,
} from '@/redux/rtk';
import { MGA_ZONE_TO_WKT } from '@/constants/mga';

const EPSGs = {
  '7850': {
    crs: MGA_ZONE_TO_WKT[50],
    zone: 'Zone 50',
    description: 'WA (Perth, Geraldton, Albany)',
  },
  '7851': {
    crs: MGA_ZONE_TO_WKT[51],
    zone: 'Zone 51',
    description: 'WA (Kalgoorlie, Esperance, Port Hedland)',
  },
  '7852': {
    crs: MGA_ZONE_TO_WKT[52],
    zone: 'Zone 52',
    description: 'WA/NT Border (Kununurra, Uluru)',
  },
  '7853': {
    crs: MGA_ZONE_TO_WKT[53],
    zone: 'Zone 53',
    description: 'NT/SA (Darwin, Alice Springs, Adelaide)',
  },
  '7854': {
    crs: MGA_ZONE_TO_WKT[54],
    zone: 'Zone 54',
    description: 'NSW/VIC/QLD/SA (Broken Hill, Mildura, Mount Isa)',
  },
  '7855': {
    crs: MGA_ZONE_TO_WKT[55],
    zone: 'Zone 55',
    description:
      'NSW/VIC/QLD/TAS (Melbourne, Canberra, Brisbane, Hobart, Wagga Wagga)',
  },
  '7856': {
    crs: MGA_ZONE_TO_WKT[56],
    zone: 'Zone 56',
    description: 'NSW/QLD (Sydney, Newcastle, Gold Coast, Wollongong)',
  },
};
const SCALEs = {
  m: 1,
  mm: 1000,
};
interface Place2dPlanFormValues {
  epsg: keyof typeof EPSGs | '';
  scale: keyof typeof SCALEs;
  easting: string;
  northing: string;
}

const validationSchema = yup.object({
  epsg: yup.string().required('EPSG is required'),
  scale: yup.string().required('Scale is required'),
  easting: yup
    .number()
    .typeError('Easting is required')
    .required('Easting is required')
    .min(0, 'Easting must be between 0 and 1,000,000 m')
    .max(1_000_000, 'Easting must be between 0 and 1,000,000 m'),
  northing: yup
    .number()
    .typeError('Northing is required')
    .required('Northing is required')
    .min(0, 'Northing must be between 0 and 10,000,000 m')
    .max(10_000_000, 'Northing must be between 0 and 10,000,000 m'),
});

export const Place2dPlanOnMapButton = ({
  className,
  assetId,
  projectId,
}: {
  className?: string;
  assetId: string;
  projectId: number;
}) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [generate2dPlanOnMap, { error, reset }] =
    useGenerate2dPlanOnMapMutation();
  const { data: placedPlans } = useGet2dPlansOnMapQuery(
    { projectId },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  const isPlaced = !!placedPlans?.assetIds.includes(assetId);

  const initialValues: Place2dPlanFormValues = {
    epsg: '',
    scale: 'mm',
    easting: '',
    northing: '',
  };

  const handleSubmit = async (values: Place2dPlanFormValues) => {
    setLoading(true);
    const result = await generate2dPlanOnMap({
      projectId,
      assetId,
      crs: EPSGs[values.epsg].crs,
      scale: Number(SCALEs[values.scale]),
      easting: Number(values.easting),
      northing: Number(values.northing),
    });
    setLoading(false);
    if (!('error' in result)) {
      setShow(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          values,
          setFieldValue,
          handleSubmit: formikSubmit,
          handleChange,
          handleBlur,
          touched,
          errors,
          setFieldTouched,
        }) => (
          <Modal
            centered
            show={show}
            onHide={() => setShow(false)}
            className='place-2d-plan-on-map-modal'
          >
            <Modal.Header>
              <h3>Place 2D Plan on 2D Map</h3>
            </Modal.Header>
            <Modal.Body className='px-0'>
              <div
                className={cn({ 'is-invalid': touched.epsg && errors.epsg })}
              >
                <p className='font-16 mb-1'>
                  Select the Projected Coordinate Reference System:
                </p>
                <Dropdown>
                  <Dropdown.Toggle
                    variant='light'
                    id='epsg'
                    className={cn({
                      'border-danger': touched.epsg && errors.epsg,
                    })}
                    onBlur={() => setFieldTouched('epsg', true)}
                  >
                    {values.epsg ? `EPSG / MGA: ${values.epsg}` : 'EPSG / MGA'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.entries(EPSGs).map(([key, value]) => (
                      <Dropdown.Item
                        key={key}
                        onClick={() => setFieldValue('epsg', key)}
                      >
                        <div className='d-flex flex-column'>
                          <div className='font-weight-bold'>{key}</div>
                          <div className='font-12'>{value.zone}</div>
                          <div className='font-12'>{value.description}</div>
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {touched.epsg && errors.epsg && (
                  <p className='Control-Feedback isFailed'>{errors.epsg}</p>
                )}
              </div>
              <div
                className={cn('mt-4', {
                  'is-invalid': touched.scale && errors.scale,
                })}
              >
                <p className='font-16 mb-1'>
                  Select Units (for DWG Model Space):
                </p>
                <Dropdown>
                  <Dropdown.Toggle
                    variant='light'
                    id='scale'
                    className={cn({
                      'border-danger': touched.scale && errors.scale,
                    })}
                    onBlur={() => setFieldTouched('scale', true)}
                  >
                    {values.scale}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.keys(SCALEs).map((key) => (
                      <Dropdown.Item
                        key={key}
                        onClick={() => setFieldValue('scale', key)}
                      >
                        {key}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {touched.scale && errors.scale && (
                  <p className='Control-Feedback isFailed'>{errors.scale}</p>
                )}
              </div>
              <p className='font-16 mb-1 mt-4'>Enter truncation (m):</p>
              {/* @ts-expect-error: untyped */}
              <Control
                required
                label='Eastings'
                as='input'
                type='number'
                name='easting'
                value={values.easting}
                onChange={handleChange}
                onBlur={handleBlur}
                className='mt-1'
                hint='0 – 1,000,000'
                isFailed={!!(touched.easting && errors.easting)}
                validationError={touched.easting ? errors.easting : undefined}
                isRequiredMarkHidden={true}
              />
              {/* @ts-expect-error: untyped */}
              <Control
                required
                label='Northings'
                as='input'
                type='number'
                name='northing'
                value={values.northing}
                onChange={handleChange}
                onBlur={handleBlur}
                hint='0 – 10,000,000'
                isFailed={!!(touched.northing && errors.northing)}
                validationError={touched.northing ? errors.northing : undefined}
                isRequiredMarkHidden={true}
              />
              {!!error && (
                <p className='Control-Feedback isFailed mt-3'>
                  {(error as LarkiErrorResponse).data?.message ??
                    'Failed to place plan on map. Please try again.'}
                </p>
              )}
            </Modal.Body>
            <Modal.Footer className='p-0'>
              <Button variant='secondary' onClick={() => setShow(false)}>
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={() => formikSubmit()}
                loading={loading}
              >
                Place
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
      <Button
        variant='link'
        aria-label={isPlaced ? 'Already on 2D Map' : 'Put on 2D Map'}
        className={cn('place-2d-plan-on-map-button', className, {
          placed: isPlaced,
        })}
        onClick={() => {
          if (isPlaced) return;
          reset();
          setShow(true);
        }}
      >
        <Icon
          icon='place-on-map'
          hasIconClass={false}
          className='PlaceOnMapIcon'
        />
      </Button>
    </>
  );
};
